import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { useTourMode } from "../../../../contexts/TourModeContext";

export interface GenerationStatusResponse {
  ticker: string;
  isGenerating: boolean;
  startTime?: number;
  elapsedSeconds?: number;
  timeoutSeconds: number;
}

interface UseGenerationStatusOptions {
  /**
   * Polling interval in milliseconds. Set to false to disable polling.
   * Default: 15000 (15 seconds)
   */
  pollingInterval?: number | false;
}

/**
 * Hook to check if a stock analysis report generation is currently in progress.
 * This is the primary source of truth for generation state.
 *
 * Features:
 * - Continuous polling (every 15s by default) to detect generation completion
 * - Immediately refreshable via invalidateGenerationStatus
 * - Handles navigation away/back scenarios seamlessly
 */
export function useGenerationStatus(
  ticker: string,
  options?: UseGenerationStatusOptions
) {
  const { isTourMode } = useTourMode();
  const pollingInterval = options?.pollingInterval ?? 15000;

  return useQuery<GenerationStatusResponse, Error>({
    queryKey: ["stockAnalysisGenerationStatus", ticker, isTourMode],
    queryFn: async () => {
      // In tour mode, return not generating so we show the report
      if (isTourMode) {
        return {
          ticker,
          isGenerating: false,
          timeoutSeconds: 300,
        };
      }

      const response = await api.get<GenerationStatusResponse>(
        "/stock-analysis-reports/generation-status",
        {
          params: { ticker },
        }
      );
      return response.data;
    },
    enabled: isTourMode || !!ticker,
    staleTime: isTourMode ? Infinity : 0, // Never stale in tour mode
    gcTime: isTourMode ? Infinity : 0, // Don't cache in normal mode
    refetchOnMount: !isTourMode,
    refetchOnWindowFocus: !isTourMode,
    // Continuous polling to detect generation completion (disabled in tour mode)
    refetchInterval: isTourMode ? false : pollingInterval,
    refetchIntervalInBackground: false, // Don't poll when window not focused
  });
}

/**
 * Helper hook to get the invalidation function for generation status.
 * Call this after starting a generation to immediately update the status.
 */
export function useInvalidateGenerationStatus() {
  const queryClient = useQueryClient();
  const { isTourMode } = useTourMode();

  return (ticker: string) => {
    // IMPORTANT: Query key must include isTourMode to match useGenerationStatus
    queryClient.invalidateQueries({
      queryKey: ["stockAnalysisGenerationStatus", ticker, isTourMode],
    });
  };
}
